import React, { useMemo, useState } from 'react';
import { Link } from 'react-router-dom';
import { Loader2, Search, Inbox, Star } from 'lucide-react';
import { fmtDate } from '@/lib/utils';
import { CASE_STATUS_LABELS, CASE_STATUS_STYLES, priorityMeta, inputClass } from './caseConstants';
import { CASE_TABS, matchesTab, isCaseOverdue } from '@/lib/caseQueue';

// The working queue: a search box over the case list, already narrowed to the
// status chosen above, newest activity first. Every row opens the case workspace.
export default function CasesQueue({ cases = [], isLoading, status = 'new', initialQuery = '' }) {
  const [q, setQ] = useState(initialQuery);

  const term = q.trim().toLowerCase();
  const filtered = useMemo(
    () =>
      cases.filter(
        (c) =>
          matchesTab(c, status) &&
          (!term ||
            [c.reference_code, c.subject, c.customer_name, c.customer_email, c.legacy_reference_code]
              .filter(Boolean)
              .join(' ')
              .toLowerCase()
              .includes(term))
      ),
    [cases, status, term]
  );

  const activeTab = CASE_TABS.find((t) => t.id === status);

  return (
    <div>
      {/* Search sits directly over the list, which is already narrowed to the
          status card chosen above. */}
      <div className="relative mb-3">
        <Search className="w-3.5 h-3.5 text-white/30 absolute left-3 top-1/2 -translate-y-1/2" />
        <input
          value={q}
          onChange={(e) => setQ(e.target.value)}
          placeholder="Search by reference, subject or customer…"
          className={`${inputClass} pl-9`}
        />
      </div>

      {isLoading ? (
        <div className="flex justify-center py-12">
          <Loader2 className="w-5 h-5 animate-spin text-primary" />
        </div>
      ) : filtered.length === 0 ? (
        <p className="text-white/30 text-xs py-12 text-center border border-white/5 bg-[#111115]">
          {status === 'feedback'
            ? 'No cases have been reopened by customer feedback — all clear.'
            : `No cases in ${activeTab?.label || 'this view'}${term ? ' matching your search' : ''}.`}
        </p>
      ) : (
        <div className="space-y-2">
          {filtered.map((c) => {
            const priority = priorityMeta(c.priority);
            const overdue = isCaseOverdue(c);
            return (
              <Link
                key={c.id}
                to={`/admin/crm/cases/${c.id}`}
                className={`flex items-center gap-3 border bg-[#111115] hover:border-primary/40 px-4 py-3 transition-colors ${
                  overdue ? 'border-red-500/40' : 'border-white/5'
                }`}
              >
                <Inbox className="w-4 h-4 text-white/20 shrink-0" />
                <div className="min-w-0 flex-1">
                  <div className="flex items-center gap-2">
                    <span className="text-primary text-[10px] font-bold tracking-widest shrink-0">
                      {c.reference_code}
                    </span>
                    <p className="text-white text-sm truncate">{c.subject || 'No subject'}</p>
                  </div>
                  <p className="text-white/40 text-xs truncate mt-0.5">
                    {c.customer_name || c.customer_email || 'Unknown customer'}
                  </p>
                  {status === 'feedback' && (
                    <p className="text-amber-300/80 text-[10px] mt-1 flex items-center gap-1.5">
                      <Star className="w-3 h-3 fill-amber-300/80" />
                      RATED {c.feedback_rating}/5
                      {c.feedback_comments ? ` · ${c.feedback_comments}` : ''}
                    </p>
                  )}
                </div>
                {overdue && (
                  <span className="text-[9px] font-bold tracking-widest text-red-400 shrink-0 hidden sm:block">
                    OVERDUE
                  </span>
                )}
                <span className={`w-2 h-2 rounded-full shrink-0 ${priority.dot}`} title={priority.label} />
                <span
                  className={`text-[9px] font-bold tracking-widest px-2 py-1 border shrink-0 ${
                    CASE_STATUS_STYLES[c.status] || 'border-white/10 text-white/40'
                  }`}
                >
                  {(CASE_STATUS_LABELS[c.status] || c.status || '—').toUpperCase()}
                </span>
                <span className="text-white/30 text-[10px] shrink-0 hidden sm:block">
                  {fmtDate(c.last_activity_at || c.created_date)}
                </span>
              </Link>
            );
          })}
        </div>
      )}
    </div>
  );
}